import Avatar from '@mui/joy/Avatar';
import Box from '@mui/joy/Box';
import IconButton from '@mui/joy/IconButton';
import Stack from '@mui/joy/Stack';
import Sheet from '@mui/joy/Sheet';
import Typography from '@mui/joy/Typography';
import CelebrationOutlinedIcon from '@mui/icons-material/CelebrationOutlined';
import FavoriteBorderIcon from '@mui/icons-material/FavoriteBorder';
import InsertDriveFileRoundedIcon from '@mui/icons-material/InsertDriveFileRounded';
import ImageRoundedIcon from '@mui/icons-material/ImageRounded';
import DownloadRoundedIcon from '@mui/icons-material/DownloadRounded';
import WarningIcon from '@mui/icons-material/Warning';
import DangerousIcon from '@mui/icons-material/Dangerous';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import EditRoundedIcon from '@mui/icons-material/EditRounded';
import DeleteOutlineRoundedIcon from '@mui/icons-material/DeleteOutlineRounded';
import AccessTimeRoundedIcon from '@mui/icons-material/AccessTimeRounded';

import { format, formatDistanceToNow } from 'date-fns';
import { ru } from 'date-fns/locale'; // Для русского языка
import { useContext, useEffect, useRef, useState } from 'react';
import { API_URL, APIMarkMessageAsDangerous } from '../../api';
import { Alert, Chip, Dropdown, Menu, MenuButton, MenuItem, Tooltip } from '@mui/joy';
import { LayoutContext } from '../../context';
import { canSeeDangerMarks } from '../../utils/dangerVisibility';


export const formatMessageDate = (dateString) => {
  const date = new Date(dateString)
  const now = new Date()
  
  // Разница в миллисекундах
  const diff = now - date

  // Защита от рассинхронизации часов клиента и сервера:
  // «в будущем» или меньше минуты → «только что» (баг #5, чтобы не было «через ...»)
  if (diff < 60 * 1000) return 'только что'

  // Если прошло меньше 24 часов
  if (diff < 24 * 60 * 60 * 1000) {
    return formatDistanceToNow(date, { 
      addSuffix: true, 
      locale: ru // Опционально - для локализации
    })
  }
  
  // Если прошло больше 24 часов, но меньше 7 дней
  if (diff < 7 * 24 * 60 * 60 * 1000) {
    return format(date, 'EEEE в HH:mm', { locale: ru }) // "Понедельник в 14:30"
  }
  
  try{
    // Если прошло больше недели
    return format(date, 'dd.MM.yyyy в HH:mm', { locale: ru }) // "19.07.2025 в 20:45"
  }
  catch {
    return null
  }
}

// Форматирование секунд в человекочитаемое «1ч 23мин» / «45 мин» / «12 сек»
export const formatResponseDuration = (seconds) => {
  if (seconds == null) return null;
  const s = Math.max(0, Math.round(seconds));
  if (s < 60) return `${s} сек`;
  const m = Math.round(s / 60);
  if (m < 60) return `${m} мин`;
  const h = Math.floor(m / 60);
  const mm = m % 60;
  return mm ? `${h}ч ${mm}мин` : `${h}ч`;
};



export default function ChatBubble({ message, isYou, onEdit, onDelete, onDangerChanged }) {
  const [isHovered, setIsHovered] = useState(false);
  const [isLiked, setIsLiked] = useState(false);
  const [isCelebrated, setIsCelebrated] = useState(false);
  const [isMarkingDangerous, setIsMarkingDangerous] = useState(false);
  const { token, account, isPatient } = useContext(LayoutContext);
  
  const [menuOpen, setMenuOpen] = useState(false);
  const [dangerError, setDangerError] = useState('');
  const dangerBusyRef = useRef(false);
  const dangerEpochRef = useRef(0);
  const isStaff = canSeeDangerMarks(account) && !isPatient;
  const canMarkDanger = isStaff && message?.type_sender !== 'system'
    && message?.can_mark_danger === true;
  const hasMessageActions = canMarkDanger || Boolean(message?.can_edit && onEdit)
    || Boolean(message?.can_delete && onDelete);

  useEffect(() => {
    dangerEpochRef.current += 1;
    dangerBusyRef.current = false;
    setMenuOpen(false); setDangerError(''); setIsMarkingDangerous(false);
    return () => { dangerEpochRef.current += 1; };
  }, [message?.id]);

  const handleMarkAsDangerous = async (target) => {
    if (!message?.id || !canMarkDanger || dangerBusyRef.current) return;
    const epoch = dangerEpochRef.current;
    dangerBusyRef.current = true;
    setMenuOpen(false); setDangerError(''); setIsMarkingDangerous(true);
    try {
      const response = await APIMarkMessageAsDangerous(message.id, token, target);
      if (epoch !== dangerEpochRef.current) return;
      if (!response?.success || !response.message?.id) {
        throw new Error('Сервер не подтвердил изменение отметки');
      }
      onDangerChanged?.(response);
    } catch (error) {
      if (epoch !== dangerEpochRef.current) return;
      setDangerError(error?.response?.data?.error || error?.message || 'Не удалось изменить отметку');
    } finally {
      if (epoch === dangerEpochRef.current) {
        dangerBusyRef.current = false;
        setIsMarkingDangerous(false);
      }
    }
  };

  // Функция для отображения вложений
  const renderAttachments = () => {
    if (!message?.attachments || message?.attachments?.length === 0) return null;
    
    return (
      <Stack spacing={1} sx={{ mt: 1 }}>
        {message?.attachments?.map((attachment) => (
          <Sheet
            key={attachment.id}
            variant="outlined"
            sx={{
              p: 1,
              borderRadius: 'md',
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              backgroundColor: 'background.level1',
            }}
          >
            {attachment.type === 'photo' ? (
              <Box 
                sx={{ 
                  mt: 1, 
                  borderRadius: 'md', 
                  overflow: 'hidden',
                  '&:hover': {
                    'cursor': 'pointer'
                  }
                }}
                onClick={() => window.open(`${API_URL}${attachment.url}`, '_blank')}
              >
                <img
                  src={`${API_URL}${attachment.url}`}
                  alt={attachment.name}
                  style={{
                    maxWidth: '100%',
                    maxHeight: '200px',
                    borderRadius: '8px'
                  }}
                  onError={(e) => {
                    e.target.style.display = 'none';
                  }}
                />
                <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                  <Typography level="body-xs" noWrap>
                    {attachment.name}
                  </Typography>
                </Box>
              </Box>
            ) : (
              <Box sx={{ mt: 1, borderRadius: 'md', overflow: 'hidden' }}>
                <InsertDriveFileRoundedIcon color="primary" />
                <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                  <Typography level="body-xs" noWrap>
                    {attachment.name}
                  </Typography>
                </Box>
                <IconButton
                  size="sm"
                  variant="plain"
                  onClick={() => window.open(`${API_URL}${attachment.url}`, '_blank')}
                  title="Скачать файл"
                >
                  <DownloadRoundedIcon />
                </IconButton>
              </Box>
            )}
          </Sheet>
        ))}
      </Stack>
    );
  };

  if (message?.type_sender == "system") {
    return (
      <Sheet
        color={'primary'}
        variant={'soft'}
        sx={{
          opacity: 0.7,
          p: 1.25,
          borderRadius: 'lg',
          width: '50vw',
          maxWidth: 200
        }}
      >
        <Typography
          level="body-sm"
          color='primary.500'
          sx={{
            wordBreak: "break-all"
          }}
        >
          {message?.content}
        </Typography>
        <Typography level="body-xs">{new Date(message?.date_create).toLocaleString('ru-RU', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })}</Typography>
      </Sheet>
    )
  }

  return (
    <Box sx={{ maxWidth: { xs: "100%", sm: '60%' }, minWidth: 'auto' }}>
      <Stack
        direction="row"
        spacing={2}
        sx={{ 
          justifyContent: isYou ? 'flex-end' : 'flex-start',
          mb: 0.25
        }}
      >
        <Typography level="body-xs">
          {message?.sender?.name}
        </Typography>

        {message?.is_dangerous && isStaff && (
          <Tooltip
            title={message.danger_source === 'manual'
              ? 'Отмечено сотрудником вручную'
              : `Совпавшие правила: ${(message.danger_keywords || []).join(', ') || 'активный список'}`}
            arrow
          >
            <Chip size="sm" variant="soft" color="danger"
              startDecorator={<WarningIcon sx={{ fontSize: 15 }} />}>
              {message.danger_source === 'manual' ? 'Отмечено вручную' : 'По ключевым словам'}
            </Chip>
          </Tooltip>
        )}
      </Stack>
      
      <Stack
        sx={{ position: 'relative', mt: isYou ? 0 : 1 }} direction={isYou ? 'row-reverse' : 'row'}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        <Stack direction={'column'} sx={{ alignItems: isYou ? 'flex-end' : 'flex-start', }}>

          <Stack direction={isYou ? 'row-reverse' : 'row'} sx={{ alignItems: 'center' }}>
            <Sheet
              color={isYou ? 'primary' : 'neutral'}
              variant={isYou ? 'solid' : 'soft'}
              sx={[
                {
                  p: 1.25,
                  borderRadius: 'lg',
                  minWidth: 0,
                  overflowWrap: 'anywhere',
                  outline: message?.is_dangerous && isStaff ? '2px solid' : 'none',
                  outlineColor: 'danger.500',
                  boxShadow: message?.is_dangerous && isStaff ? '0 0 0 4px rgba(220,38,38,.09)' : 'none',
                },
                isYou
                  ? {
                      borderTopRightRadius: 0,
                    }
                  : {
                      borderTopRightRadius: 'lg',
                    },
                isYou
                  ? {
                      borderTopLeftRadius: 'lg',
                    }
                  : {
                      borderTopLeftRadius: 0,
                    },
                isYou
                  ? {
                      backgroundColor: 'var(--joy-palette-primary-solidBg)',
                    }
                  : {
                      backgroundColor: 'background.body',
                    },
              ]}
            >
              <Typography
                level="body-sm"
                sx={[
                  isYou
                    ? {
                        color: 'var(--joy-palette-common-white)',
                      }
                    : {
                        color: 'var(--joy-palette-text-primary)',
                      },
                ]}
              >
                {message?.content}
              </Typography>

              {/* Отображение вложений */}
              {renderAttachments()}
            </Sheet>

            {hasMessageActions && (
              <Box sx={{ px: 0.5, flexShrink: 0, alignSelf: 'flex-start' }}>
                <Dropdown open={menuOpen} onOpenChange={(_event, open) => setMenuOpen(open)}>
                  <MenuButton
                    aria-label="Действия с сообщением"
                    variant="plain"
                    color="neutral"
                    disabled={isMarkingDangerous}
                    sx={{ width: 44, minWidth: 44, height: 44, p: 0,
                          borderRadius: 'lg', opacity: 1,
                          '&:hover': { bgcolor: 'background.level2' } }}
                  >
                    <MoreVertIcon />
                  </MenuButton>
                  <Menu size="sm" placement={isYou ? 'bottom-end' : 'bottom-start'}
                    sx={{ zIndex: 10016, minWidth: 230, maxWidth: 'calc(100vw - 24px)' }}>
                    {canMarkDanger && (
                      <MenuItem disabled={isMarkingDangerous}
                        onClick={() => handleMarkAsDangerous(!message.is_dangerous)}>
                        <WarningIcon />
                        {message.is_dangerous ? 'Снять опасную отметку' : 'Пометить как опасное'}
                      </MenuItem>
                    )}
                    {canMarkDanger && message.danger_manual !== null && message.danger_manual !== undefined && (
                      <MenuItem disabled={isMarkingDangerous}
                        onClick={() => handleMarkAsDangerous(null)}>
                        <DangerousIcon /> Вернуть автопроверку
                      </MenuItem>
                    )}
                    {message?.can_edit && onEdit && (
                      <MenuItem onClick={() => { setMenuOpen(false); onEdit(message); }}>
                        <EditRoundedIcon /> Редактировать
                      </MenuItem>
                    )}
                    {message?.can_delete && onDelete && (
                      <MenuItem color="danger" onClick={() => { setMenuOpen(false); onDelete(message); }}>
                        <DeleteOutlineRoundedIcon /> Удалить
                      </MenuItem>
                    )}
                  </Menu>
                </Dropdown>
              </Box>
            )}
          </Stack>
          
          {dangerError && (
            <Alert role="alert" size="sm" color="danger" variant="soft"
              sx={{ mt: 1, maxWidth: '100%', overflowWrap: 'anywhere' }}>
              {dangerError}
            </Alert>
          )}
          
          <Stack direction="row" spacing={0.75} alignItems="center" sx={{ flexWrap: 'wrap' }}>
            <Typography level="body-xs">
              {formatMessageDate(message?.date_create)}
            </Typography>

            {/* Метка «изменено» */}
            {message?.is_edited && (
              <Typography level="body-xs" color="neutral" sx={{ fontStyle: 'italic' }}>
                (изменено)
              </Typography>
            )}

            {/* Время ответа врача (показываем только сотрудникам, не пациенту) */}
            {message?.response_time_seconds != null && account && !isPatient && (
              <Tooltip title="Время ответа врача с момента сообщения пациента" arrow>
                <Stack direction="row" spacing={0.25} alignItems="center">
                  <AccessTimeRoundedIcon sx={{ fontSize: 12, color: 'text.tertiary' }} />
                  <Typography level="body-xs" color="neutral">
                    ответ через {formatResponseDuration(message.response_time_seconds)}
                  </Typography>
                </Stack>
              </Tooltip>
            )}
          </Stack>
        </Stack>
      </Stack>
    </Box>
  );
}
